import { doc, onSnapshot } from 'firebase/firestore';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { firestore } from '../services/firebase';
import type { PricingSettings } from '../shared/constants/pricing';

interface PricingContextProps {
  pricingSettings: PricingSettings | null;
  loading: boolean;
  error: Error | null;
}

const PricingContext = createContext<PricingContextProps>({
  pricingSettings: null,
  loading: true,
  error: null,
});

export const usePricing = () => useContext(PricingContext);

interface PricingProviderProps {
  children: React.ReactNode;
}

export const PricingProvider: React.FC<PricingProviderProps> = ({ children }) => {
  const [pricingSettings, setPricingSettings] = useState<PricingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const docRef = doc(firestore, 'platformSettings', 'pricing');

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const mappedSettings: PricingSettings = {
            ...data,
            cost_per_credit_in_base:
              data.cost_per_credit_in_base ?? data.cost_per_credit_aud ?? 1.0,
            usd_to_base_exchange_rate:
              data.usd_to_base_exchange_rate ?? data.usd_to_aud_rate ?? 1.4,
          } as PricingSettings;
          setPricingSettings(mappedSettings);
          setError(null);
        } else {
          // Optional: handle missing settings document gracefully
          setError(new Error('Pricing settings not found.'));
        }
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <PricingContext.Provider value={{ pricingSettings, loading, error }}>
      {children}
    </PricingContext.Provider>
  );
};
